import React, { useState, useEffect } from 'react';
import { Star, Check, Clock, Users, Trash2 } from 'lucide-react';
import { RecipePlaceholderCard } from './RecipePlaceholder';
import { recipes as recipeApi } from '../utils/api';

const RecipeCard = ({ recipe, onClick, userLabels = [], hideCategory = false }) => {
  // Check if recipe has an image (support images array, image_url from mobile, and has_image flag)
  const hasImage = recipe.has_image || (recipe.images && recipe.images.length > 0) || !!recipe.image_url;
  
  // Initialize thumbnail from existing data if available
  const initialThumbnail = (recipe.images && recipe.images.length > 0)
    ? (recipe.images[0].startsWith('data:') || recipe.images[0].startsWith('http') ? recipe.images[0] : `data:image/jpeg;base64,${recipe.images[0]}`)
    : recipe.image_url || null;
  
  const [thumbnail, setThumbnail] = useState(initialThumbnail);
  const [loadingThumb, setLoadingThumb] = useState(false);
  
  // Load thumbnail lazily when card mounts if recipe has an image but no data
  useEffect(() => {
    // Skip if we already have thumbnail or if there's no image
    if (thumbnail || !recipe.has_image) {
      return;
    }
    
    let cancelled = false;
    
    // Lazy load thumbnail if has_image flag is true
    const loadThumbnail = async () => {
      try {
        const res = await recipeApi.getThumbnail(recipe.recipe_id);
        if (!cancelled && res.data?.thumbnail) {
          setThumbnail(res.data.thumbnail);
        }
      } catch (err) {
        console.error('Failed to load thumbnail:', err);
      } finally {
        if (!cancelled) {
          setLoadingThumb(false);
        }
      }
    };
    
    setLoadingThumb(true);
    loadThumbnail();
    
    return () => { cancelled = true; };
  }, [recipe.recipe_id, recipe.has_image, thumbnail]);
  
  const getCategoryLabel = (category) => {
    const labels = {
      main_dish: 'Main Dish',
      appetizer: 'Appetizer',
      dessert: 'Dessert',
      salad: 'Salad',
      soup: 'Soup',
      side: 'Side',
      breakfast: 'Breakfast',
      snack: 'Appetizer', // Maps to Appetizer
      beverage: 'Beverage',
      other: 'Other',
    };
    return labels[category] || 'Other';
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={16}
        className={i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
      />
    ));
  };

  // Get label details from userLabels
  const getLabelInfo = (labelName) => {
    const label = userLabels.find(l => l.name === labelName);
    return label || { name: labelName, color: '#6b7280', icon: null };
  };

  return (
    <div
      onClick={onClick}
      data-testid={`recipe-card-${recipe.recipe_id}`}
      className="recipe-card bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all border border-gray-100"
    >
      {/* Image or Placeholder */}
      <div className="relative">
        {thumbnail ? (
          <div className="h-32">
            <img
              src={thumbnail}
              alt={recipe.title}
              className={`w-full h-full object-cover ${recipe.deleted ? 'opacity-50 grayscale' : ''}`}
            />
          </div>
        ) : hasImage && loadingThumb ? (
          <div className="h-32 bg-gray-100 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className={recipe.deleted ? 'opacity-50 grayscale' : ''}>
            <RecipePlaceholderCard category={recipe.category} />
          </div>
        )}
        {recipe.deleted ? (
          <div className="absolute top-2 right-2 bg-gray-500 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium">
            <Trash2 size={14} />
            Deleted
          </div>
        ) : recipe.cooked && (
          <div className="absolute top-2 right-2 bg-emerald-600 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium">
            <div className="w-3.5 h-3.5 border-2 border-white rounded flex items-center justify-center">
              <Check size={10} strokeWidth={3} />
            </div>
            Cooked
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-4 ${recipe.deleted ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-base font-semibold text-gray-900 line-clamp-2 flex-1">
            {recipe.title}
          </h3>
        </div>

        {recipe.description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{recipe.description}</p>
        )}

        {/* Category Badge & Labels - side by side */}
        {(recipe.labels?.length > 0 || !hideCategory) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            {/* Category Badge - hidden when sorting by type */}
            {!hideCategory && (
              <span className="inline-block bg-amber-50 text-amber-700 text-xs px-2 py-1 rounded-full font-medium border border-amber-200">
                {getCategoryLabel(recipe.category)}
              </span>
            )}
            
            {/* Labels */}
            {recipe.labels && recipe.labels.length > 0 && (
              <>
                {recipe.labels
                  .filter(labelName => labelName !== 'Up Next') // Hide Up Next - it has its own section
                  .slice(0, 3)
                  .map((labelName) => {
                  const labelInfo = getLabelInfo(labelName);
                  return (
                    <span
                      key={labelName}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: labelInfo.color }}
                    >
                      {labelInfo.icon && <span>{labelInfo.icon}</span>}
                      {labelName}
                    </span>
                  );
              })}
              {recipe.labels.filter(l => l !== 'Up Next').length > 3 && (
                <span className="text-xs text-gray-500 px-1">+{recipe.labels.filter(l => l !== 'Up Next').length - 3}</span>
              )}
            </>
          )}
          </div>
        )}

        {/* Rating - only show if rating is greater than 0 */}
        {recipe.rating > 0 && (
          <div className="flex items-center gap-1 mb-3">
            {renderStars(recipe.rating)}
          </div>
        )}

        {/* Meta Info */}
        <div className="flex items-center gap-4 text-sm text-gray-500">
          {recipe.prep_time > 0 && (
            <div className="flex items-center gap-1">
              <Clock size={14} />
              <span>{recipe.prep_time + (recipe.cook_time || 0)} min</span>
            </div>
          )}
          {recipe.servings > 0 && (
            <div className="flex items-center gap-1">
              <Users size={14} />
              <span>{recipe.servings}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeCard;